import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { loadConnectAndInitialize, type StripeConnectInstance } from '@stripe/connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { Note, Spinner } from '@wyocspay/ui';
import { ApiError, api } from './api.js';

/**
 * One Connect instance for the whole panel.
 *
 * Every embedded component - onboarding, account management, payments,
 * payouts, balances, disputes, documents, tax - renders from this single
 * AccountSession. That is what keeps the merchant inside WyoCSPay instead of
 * sending them to the Stripe dashboard, while the sensitive parts (identity
 * documents, bank details, card data) stay inside Stripe's own iframes and
 * never touch our servers.
 *
 * `fetchClientSecret` is called again by Stripe.js whenever the session needs
 * refreshing, so this must stay a live callback rather than a one-shot value.
 */

interface ConnectState {
  instance: StripeConnectInstance | null;
  status: 'loading' | 'ready' | 'unavailable';
  message: string | null;
}

const Ctx = createContext<ConnectState>({ instance: null, status: 'loading', message: null });

export function useConnect(): ConnectState {
  return useContext(Ctx);
}

/**
 * The language Stripe's embedded components speak.
 *
 * Kept beside the panel's own language rather than derived from the browser,
 * because the two appearing in different languages on one screen looks like a
 * defect and makes Stripe's error messages read as ours.
 */
const CONNECT_LOCALE = 'en-US';

export function ConnectProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConnectState>({ instance: null, status: 'loading', message: null });

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const session = await api.connectSession();
    return session.client_secret;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await api.connectSession();
        if (cancelled) return;

        const instance = loadConnectAndInitialize({
          publishableKey: session.publishable_key,
          fetchClientSecret,

          /**
           * Say which language, rather than letting Stripe read the browser's.
           *
           * Without this, a merchant on a Bulgarian-configured machine sees the
           * panel in English and Stripe's embedded components in Bulgarian, in
           * the same screen, sometimes in the same card. Worse, the error text
           * that matters most - the one explaining why verification failed -
           * arrives in a language the surrounding page never uses, so it reads
           * as a fault in the platform rather than a message from Stripe.
           *
           * The merchant panel is English throughout, so the components are
           * told English. When the panel gains real localisation this should
           * follow whatever it settles on, which is why it is a named constant
           * rather than a string buried in the call.
           */
          locale: CONNECT_LOCALE,

          appearance: {
            variables: {
              colorPrimary: '#2f6188',
              colorText: '#3c4952',
              colorBackground: '#ffffff',
              colorSecondaryText: '#6d7880',
              colorBorder: '#dde1e4',
              colorDanger: '#c02929',
              fontFamily: "'Source Sans 3', -apple-system, sans-serif",
              borderRadius: '6px',
              buttonPrimaryColorBackground: '#2f6188',
              buttonPrimaryColorText: '#ffffff',

              /**
               * Status pills, themed in full rather than by text colour alone.
               *
               * Setting only the text colour left Stripe to pick its own
               * backgrounds, and its default for a neutral state - Canceled,
               * Refunded - is a dark slab that reads as an error against this
               * white panel. Each state now carries a soft tint of the panel's
               * own hue with a darker label on top, the same shape as the
               * badges the platform draws itself, so a status looks the same
               * whether it came from our data or from Stripe's embedded view.
               *
               * The tints are light enough to sit on white and the text dark
               * enough to stay legible on the tint (all four clear WCAG AA).
               */
              badgeSuccessColorBackground: '#e7f4ec',
              badgeSuccessColorText: '#1f7a44',
              badgeSuccessColorBorder: '#bfe3cd',

              badgeWarningColorBackground: '#fdf4e3',
              badgeWarningColorText: '#96610f',
              badgeWarningColorBorder: '#f2dcae',

              badgeDangerColorBackground: '#fbe9e9',
              badgeDangerColorText: '#b3261e',
              badgeDangerColorBorder: '#f2c9c7',

              badgeNeutralColorBackground: '#eef1f3',
              badgeNeutralColorText: '#55636d',
              badgeNeutralColorBorder: '#dce1e5',
            },
          },
        });

        setState({ instance, status: 'ready', message: null });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.code === 'not_configured'
              ? 'Stripe is not configured on this platform. Add STRIPE_SECRET_KEY and STRIPE_PUBLISHABLE_KEY to the API environment.'
              : err.message
            : String(err);
        setState({ instance: null, status: 'unavailable', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchClientSecret]);

  const value = useMemo(() => state, [state]);

  if (!state.instance) {
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  return (
    <Ctx.Provider value={value}>
      <ConnectComponentsProvider connectInstance={state.instance}>{children}</ConnectComponentsProvider>
    </Ctx.Provider>
  );
}

/**
 * Wraps any embedded component with the loading and unavailable states, so
 * every view handles "Stripe is not set up" the same way instead of each one
 * inventing its own empty screen.
 */
export function ConnectGate({ children, what }: { children: ReactNode; what: string }) {
  const { status, message } = useConnect();

  if (status === 'loading') return <Spinner label={`Loading ${what}…`} />;
  if (status === 'unavailable') {
    return (
      <Note tone="danger" title={`${what} is not available`}>
        {message}
      </Note>
    );
  }
  return <>{children}</>;
}
